/**
 * Static NAV_ITEMS registry + small helpers shared by Sidebar, BottomNav and
 * any nav consumer. Kept as a pure module (no React) so it can be imported
 * from non-component code (e.g., tests) without paying provider setup cost.
 */

import {
  LayoutDashboard,
  Calendar,
  Plane,
  Wrench,
  Users,
  DollarSign,
  Radio,
  Shield,
  ShieldCheck,
  Building2,
  Fuel,
  TowerControl,
  UserCircle,
  Bell,
  Cloud,
  Calculator,
  AlertTriangle,
  Clock,
  ClipboardCheck,
  UsersRound,
  IdCard,
  BarChart3,
  History,
  Activity,
} from "lucide-react";

// Label values are i18n keys resolved at render time
const ALERTS_KEY = "nav.alerts";

export const NAV_ITEMS = {
  super_admin: [
    { path: "/superadmin", labelKey: "nav.dashboard", icon: LayoutDashboard },
    { path: "/companies", labelKey: "nav.companies", icon: Building2 },
    { path: "/all-users", labelKey: "nav.allUsers", icon: Users },
    {
      path: "/admin/alerts",
      labelKey: "nav.systemAlerts",
      icon: AlertTriangle,
      isSystemAlerts: true,
    },
    { path: "/superadmin/etm", labelKey: "nav.etmTuning", icon: Activity },
    { path: "/superadmin/flight-events", labelKey: "nav.flightEventsAudit", icon: Radio },
    { path: "/superadmin/fuel-price-simulator", labelKey: "nav.fuelPriceSimulator", icon: Fuel },
    { path: "/superadmin/aip-mx", labelKey: "nav.aipMxAudit", icon: ClipboardCheck },
    { path: "/superadmin/faa-nasr", labelKey: "nav.faaNasrAudit", icon: ClipboardCheck },
    { path: "/superadmin/web-vitals", labelKey: "nav.webVitals", icon: BarChart3 },
  ],
  ops_manager: [
    { path: "/dashboard", labelKey: "nav.occ", icon: LayoutDashboard },
    {
      path: "/calendar",
      labelKey: "nav.calendar",
      icon: Calendar,
      isCalendar: true,
      offlineAvailable: true,
    },
    { path: "/alerts", labelKey: ALERTS_KEY, icon: Bell, isAlerts: true },
    // Live Tracking: oculto del sidebar, accesible solo desde el widget en OCC.
    // La ruta /tracking sigue activa en App.js para deep-links y para la
    // navegación que dispara el LiveTrackingWidget.
    // { path: '/tracking', labelKey: 'nav.liveTracking', icon: Radio },
    { path: "/cbp", labelKey: "nav.cbp", icon: ShieldCheck, offlineAvailable: true },
    {
      path: "/hours-of-operation",
      labelKey: "nav.hoursOfOperation",
      icon: Clock,
      offlineAvailable: true,
    },
    { path: "/fleet", labelKey: "nav.fleet", icon: Plane },
    { path: "/maintenance", labelKey: "nav.maintenance", icon: Wrench },
    { path: "/weather", labelKey: "nav.weather", icon: Cloud },
    { path: "/flight-calculator", labelKey: "nav.flightCalculator", icon: Calculator },
    {
      groupKey: "reports",
      labelKey: "nav.reports",
      icon: BarChart3,
      children: [
        { path: "/fuel-analytics", labelKey: "nav.fuelAnalytics", icon: Fuel },
        { path: "/expenses", labelKey: "nav.expenses", icon: DollarSign, offlineAvailable: true },
        { path: "/hours-log", labelKey: "nav.hoursLog", icon: Clock },
        { path: "/audit", labelKey: "nav.activityLog", icon: History },
      ],
    },
    // { path: '/ai', labelKey: 'nav.aiAgent', icon: Bot }, // hidden — keep route/backend intact for later re-enable
    {
      groupKey: "people",
      labelKey: "nav.people",
      icon: UsersRound,
      children: [
        { path: "/staff", labelKey: "nav.staff", icon: IdCard },
        { path: "/owners", labelKey: "nav.owners", icon: UserCircle },
      ],
    },
  ],
  pilot: [
    { path: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
    { path: "/alerts", labelKey: ALERTS_KEY, icon: Bell, isAlerts: true },
    {
      path: "/calendar",
      labelKey: "nav.calendar",
      icon: Calendar,
      isCalendar: true,
      offlineAvailable: true,
    },
    { path: "/fleet", labelKey: "nav.myAircraft", icon: Plane },
    { path: "/flights", labelKey: "nav.flights", icon: TowerControl },
    { path: "/maintenance", labelKey: "nav.maintenance", icon: Wrench },
    { path: "/hours-log", labelKey: "nav.hoursLog", icon: Clock },
    { path: "/cbp", labelKey: "nav.cbp", icon: ShieldCheck, offlineAvailable: true },
    {
      path: "/hours-of-operation",
      labelKey: "nav.hoursOfOperation",
      icon: Clock,
      offlineAvailable: true,
    },
    { path: "/expenses", labelKey: "nav.expenses", icon: DollarSign, offlineAvailable: true },
    {
      groupKey: "people",
      labelKey: "nav.people",
      icon: UsersRound,
      children: [
        { path: "/staff", labelKey: "nav.staff", icon: IdCard },
        { path: "/owners", labelKey: "nav.owners", icon: UserCircle },
      ],
    },
    // { path: '/ai', labelKey: 'nav.aiAgent', icon: Bot }, // hidden — keep route/backend intact for later re-enable
  ],
  owner: [
    {
      path: "/calendar",
      labelKey: "nav.calendar",
      icon: Calendar,
      isCalendar: true,
      offlineAvailable: true,
    },
    { path: "/alerts", labelKey: ALERTS_KEY, icon: Bell, isAlerts: true },
    { path: "/fleet", labelKey: "nav.myAircraft", icon: Plane },
    { path: "/tracking", labelKey: "nav.liveTracking", icon: Radio },
    // expenses and fuel-analytics added dynamically based on owner permissions
  ],
  mechanic: [
    { path: "/alerts", labelKey: ALERTS_KEY, icon: Bell, isAlerts: true },
    {
      path: "/calendar",
      labelKey: "nav.calendar",
      icon: Calendar,
      isCalendar: true,
      offlineAvailable: true,
    },
    { path: "/fleet", labelKey: "nav.fleet", icon: Plane },
    { path: "/maintenance", labelKey: "nav.maintenance", icon: Wrench },
    { path: "/scheduled-inspections", labelKey: "nav.scheduledInspections", icon: ClipboardCheck },
    { path: "/staff", labelKey: "nav.staff", icon: IdCard },
  ],
  ground_support: [
    { path: "/alerts", labelKey: ALERTS_KEY, icon: Bell, isAlerts: true },
    {
      path: "/calendar",
      labelKey: "nav.calendar",
      icon: Calendar,
      isCalendar: true,
      offlineAvailable: true,
    },
    { path: "/fleet", labelKey: "nav.fleet", icon: Plane },
    { path: "/fuel-orders", labelKey: "nav.fuelOrders", icon: Fuel },
    { path: "/tracking", labelKey: "nav.liveTracking", icon: Radio },
  ],
  accountant: [
    { path: "/expenses", labelKey: "nav.expenses", icon: DollarSign, offlineAvailable: true },
  ],
};

/** Lucide icon per role — drives the badge under the user's name. */
export const ROLE_ICON = {
  super_admin: Shield,
  ops_manager: TowerControl,
  pilot: Plane,
  owner: Building2,
  mechanic: Wrench,
  ground_support: Users,
  accountant: Calculator,
};

/** True when an item (or any of its children) is reachable offline. Drives
 *  the greyed-out "Necesita conexión" state in Sidebar / BottomNav. */
export function isItemOfflineAvailable(item) {
  if (!item) return false;
  if (item.offlineAvailable) return true;
  if (item.children) return item.children.some((c) => c.offlineAvailable);
  return false;
}

/**
 * Owner-only: append optional /expenses + /fuel-analytics entries based on the
 * per-owner permission flags. Returns the role base for every other role.
 */
export function resolveNavItems(user) {
  const base = NAV_ITEMS[user?.role] || NAV_ITEMS.pilot;
  if (user?.role !== "owner") return base;
  const extras = [];
  if (user?.settings?.can_see_expenses) {
    extras.push({
      path: "/expenses",
      labelKey: "nav.expenses",
      icon: DollarSign,
      offlineAvailable: true,
    });
  }
  if (user?.settings?.can_see_fuel_analytics) {
    extras.push({ path: "/fuel-analytics", labelKey: "nav.fuelAnalytics", icon: Fuel });
  }
  return [...base, ...extras];
}
